import React, { useContext, useState, useEffect } from "react";
import { HashLink as Link } from "react-router-hash-link";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { ThemeContext } from "../ThemeContext";
import { FiMoon, FiSun, FiMenu, FiX } from "react-icons/fi";

const Background = styled.div`
  position: fixed;
  width: 100vw;
  background-color: var(--bg-color);
  height: 80px;
  padding: 0 40px;
  box-sizing: border-box;
  z-index: 100;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(150,150,150, 0.1);
  transition: background 0.3s ease;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Main = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: 900;
  margin: 0;
  letter-spacing: -1px;
  text-transform: uppercase;
  color: var(--text-color);
  
  span {
    color: var(--accent);
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color) !important;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    color: var(--accent) !important;
  }
`;

const ThemeToggle = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  
  &:hover {
    opacity: 0.8;
  }

  &.mobile-only-toggle {
    @media (max-width: 768px) {
      display: flex !important;
    }
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  font-size: 1.5rem;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 8px;
  z-index: 200;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--bg-color);
  z-index: 150;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  opacity: ${props => (props.$open ? 1 : 0)};
  pointer-events: ${props => (props.$open ? "auto" : "none")};
  transition: opacity 0.3s ease-in-out;
`;

const MobileNavLink = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color) !important;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  &:hover {
    color: var(--accent) !important;
  }
`;

const Navbar = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 200);
    };
    
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initial check
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isHome = location.pathname === "/";
  const showLogo = !isHome || scrolled;

  return (
    <Background>
      <Main>
        <Link 
          to="/" 
          style={{ 
            textDecoration: 'none', 
            opacity: showLogo ? 1 : 0, 
            transition: 'opacity 0.3s ease',
            pointerEvents: showLogo ? 'auto' : 'none'
          }}
        >
          <LogoText>Stephen<span>SJBrown</span></LogoText>
        </Link>
        <Right>
          <NavLink to="/design">design</NavLink>
          <NavLink to="/movement">movement</NavLink>
          <NavLink to="/music">music</NavLink>
          <NavLink to="/about">about</NavLink>
          <ThemeToggle onClick={toggleTheme}>
            {theme === 'light' ? <FiMoon /> : <FiSun />}
          </ThemeToggle>
        </Right>
        
        {/* Mobile Nav Button wrapper (visible only on mobile viewports) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <ThemeToggle onClick={toggleTheme} className="mobile-only-toggle" style={{ display: 'none' }}>
            {theme === 'light' ? <FiMoon /> : <FiSun />}
          </ThemeToggle>
          <MenuButton onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <FiX /> : <FiMenu />}
          </MenuButton>
        </div>
      </Main>

      <MobileMenu $open={menuOpen}>
        <MobileNavLink to="/design" onClick={() => setMenuOpen(false)}>design</MobileNavLink>
        <MobileNavLink to="/movement" onClick={() => setMenuOpen(false)}>movement</MobileNavLink>
        <MobileNavLink to="/music" onClick={() => setMenuOpen(false)}>music</MobileNavLink>
        <MobileNavLink to="/about" onClick={() => setMenuOpen(false)}>about</MobileNavLink>
      </MobileMenu>
    </Background>
  );
};

export default Navbar;
